import React, { useState, useEffect, useCallback, useRef, useContext, useMemo, memo } from "react";
import './shop.css';
import { FiHeart } from "react-icons/fi";
import { FaTimes } from "react-icons/fa";
import { FaHeart } from "react-icons/fa";
import Header from "./Header";
import mapy from './icon/mapy.png';
import { useNavigate } from 'react-router-dom';
import FilterDropdown from "./FilterDropdown";
import CartContext from "./CartContext";

// ── Defined OUTSIDE component so React never re-creates these on re-render ────
const LoadingSpinner = memo(() => (
    <div className="flipkart-spinner">
        <div className="spinner-container">
            <div className="spinner"></div>
            <div className="loading-text">Loading products...</div>
        </div>
    </div>
));

// Flipkart-style skeleton loading for products
const ProductSkeleton = memo(() => (
    <div className="product-skeleton">
        <div className="skeleton-image"></div>
        <div className="skeleton-line short"></div>
        <div className="skeleton-line medium"></div>
        <div className="skeleton-line long"></div>
        <div className="skeleton-button"></div>
    </div>
));

const SKELETON_ARRAY = [...Array(8)];
const LoadingSkeleton = memo(() => (
    <div className="flipkart-loading-skeleton">
        {SKELETON_ARRAY.map((_, index) => (
            <ProductSkeleton key={index} />
        ))}
    </div>
));
// ─────────────────────────────────────────────────────────────────────────────

// Flipkart/Amazon style inline star rating badge
const StarRating = memo(({ avg, count }) => {
    if (!avg || avg === 0 || count === 0) return null;
    const filled = Math.round(avg);
    return (
        <div className="shop-star-rating">
            <span className="shop-star-badge">
                {avg.toFixed(1)}
                <span className="shop-star-icon">★</span>
            </span>
            <span className="shop-rating-count">({count})</span>
        </div>
    );
});

// ── Module-level: runs once, not on every render ──────────────────────────────
const API_URL_CONST = process.env.REACT_APP_API_URL || 'https://pyate-backend-production-9749.up.railway.app';

const _cache = (() => {
    try {
        const raw = localStorage.getItem('shop_cache');
        if (!raw) return null;
        const p = JSON.parse(raw);
        if (Date.now() - (p.cachedAt || 0) > 5 * 60 * 1000) { localStorage.removeItem('shop_cache'); return null; }
        return p;
    } catch { return null; }
})();

// Decode userId from JWT — skips /api/get-user-id round-trip if token has it
function userIdFromToken(token) {
    try {
        if (!token) return null;
        const pl = JSON.parse(atob(token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')));
        return String(pl.user_id || pl.sub || pl.id || '');
    } catch { return null; }
}

// Memoized ProductCard — only re-renders when its own card data changes
const ProductCard = memo(({ card, onAddToCart, onToggleWishlist, onStoreClick }) => (
    <div className="shop_card-shop" key={card.id}>
        <img src={card.image_url} alt={card.name} className="card-image-shop" loading="lazy" />
        <div className="shop_container-shop" style={{ textAlign: "justify" }}>
            <a href={`/store/${card.pageid}`} style={{ textDecoration: "none", fontSize: "14pt", color: "black", fontWeight: "bold" }}>
                {card.name}
            </a><br />
            <StarRating avg={card.avg_rating} count={card.rating_count} />
            <a href={`/store/${card.pageid}`} style={{ fontSize: '7pt', color: 'gray' }}>
                {card.description}
            </a><br/>
            <a
                href={`/store/${card.pageid}`}
                style={{ textDecoration: "none", fontSize: "11pt", color: "black", cursor: "pointer" }}
                onClick={(e) => { e.preventDefault(); onStoreClick(card.pageid, card.page_name); }}
            >
                {card.page_name}
            </a><br />
            <span style={{ color: "gray", fontSize: "10pt" }}>
                <img src={mapy} width={"13px"} height={"13px"} style={{ marginRight: "5px" }} />
                {card.city}
            </span>
            <p className="card-text-shop">
                <b style={{ fontSize: "12pt", color: "black" }}>₹{card.price}</b>
                <span style={{ color: "green", padding: "10px" }}>({card.discount}% off)</span>
                <span style={{ textDecoration: "line-through" }}>M.R.P.: ₹{card.mrp}</span>
                <br />
                <button className="cart-btn-shop" onClick={() => onAddToCart(card)}>Add to Cart</button>
                <span
                    className="wishlist-icon-shop"
                    onClick={() => onToggleWishlist(card.id)}
                    title={card.isWishlist ? "Remove from Wishlist" : "Add to Wishlist"}
                >
                    {card.isWishlist ? (
                        <button className="heart_btn-shop">
                            <FaHeart style={{ fontSize: '21px', color: 'rgb(248, 14, 143)' }} />
                        </button>
                    ) : (
                        <button className="heart_btn-shop">
                            <FiHeart style={{ fontSize: '21px' }} />
                        </button>
                    )}
                </span>
            </p>
        </div>
    </div>
));

function Shop() {
    const [popupMessage, setPopupMessage] = useState("");
    const [items, setItems] = useState(_cache?.items || []);
    const [searchInput, setSearchInput] = useState('');
    const [activeSearch, setActiveSearch] = useState('');
    const [category, setCategory] = useState([]);
    const [merchant, setMerchant] = useState([]);
    const [slocation, setSlocation] = useState([]);
    const [wishlist, setWishlist] = useState([]);
    const [city, setCity] = useState([]); // <-- city filter state
    const [cities, setCities] = useState(_cache?.cities || []);
    const navigate = useNavigate();
    const token = localStorage.getItem('token');
    const [userId, setUserId] = useState(() => userIdFromToken(localStorage.getItem('token')) || localStorage.getItem('userId') || null);
    const [loading, setLoading] = useState(!_cache);
    const [error, setError] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const popupTimeoutRef = useRef(null);
    const searchDebounceRef = useRef(null);
    const [availableCategories, setAvailableCategories] = useState(_cache?.categories || []);
    const [filteredItems, setFilteredItems] = useState([]);
    const [merchants, setMerchants] = useState(_cache?.merchants || []);
    const [locations, setLocations] = useState(_cache?.locations || []);
    const itemsToDisplay = useMemo(() =>
        (merchant.length > 0 || category.length > 0 || slocation.length > 0 || city.length > 0 || activeSearch)
            ? filteredItems
            : items,
        [merchant, category, slocation, city, activeSearch, filteredItems, items]
    );
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const { setCartCount } = useContext(CartContext);


    // API_URL is now a module-level constant — no useMemo needed
    const API_URL = API_URL_CONST;


    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    const fetchItems = useCallback(async (resolvedUserId) => {
        const t0 = performance.now();
        const hasCached = !!localStorage.getItem('shop_cache');
        if (!hasCached) setLoading(true);
        setError(null);
        try {
            const tok = localStorage.getItem('token');
            const uid = resolvedUserId || localStorage.getItem('userId');
            if (!tok || !uid) { setLoading(false); return; }

            // NOTE: Using /api/get_items directly.
            // Once /api/shop_bootstrap is deployed on the server, change this URL.
            const response = await fetch(`${API_URL}/api/get_items?page=${currentPage}`, {
                headers: { Authorization: `Bearer ${tok}`, 'Content-Type': 'application/json' }
            });

            const elapsed = performance.now() - t0;
            if (elapsed > 2000) console.error(`[SLOW] /api/get_items ${(elapsed/1000).toFixed(2)}s`);

            if (!response.ok) {
                let body = ''; try { body = await response.text(); } catch(_) {}
                console.error(`[Shop] GET /api/get_items → ${response.status}: ${body}`);
                if (!hasCached) setError(`Failed to load items (${response.status}).`);
                setLoading(false);
                return;
            }

            const data = await response.json();
            const wishlistedIds = new Set(
                JSON.parse(localStorage.getItem("wishlist") || "[]").map(w => w.id)
            );
            const rawItems = Array.isArray(data) ? data : (data.items || []);
            const itemsWithWishlist = rawItems.map(item => ({ ...item, isWishlist: wishlistedIds.has(item.id) }));

            setItems(itemsWithWishlist);
            if (data.merchants)  setMerchants(data.merchants);
            if (data.cities)     setCities(data.cities);
            if (data.locations)  setLocations(data.locations);
            if (data.categories) setAvailableCategories(data.categories);

            try {
                localStorage.setItem('shop_cache', JSON.stringify({
                    items: itemsWithWishlist, merchants: data.merchants || [],
                    cities: data.cities || [], locations: data.locations || [],
                    categories: data.categories || [], cachedAt: Date.now(),
                }));
            } catch(_) {}

        } catch (err) {
            console.error('[Shop] fetchItems error:', err);
            if (!hasCached) setError("Error loading items.");
        } finally {
            setLoading(false);
        }
    }, [API_URL, currentPage]);


    const fetchFilteredItems = useCallback(async () => {
        const t0 = performance.now();
        try {
            setLoading(true);

            const params = new URLSearchParams();

            if (merchant.length > 0) {
                params.append('merchant', merchant.join(','));
            }

            if (category.length > 0) {
                params.append('category', category.join(','));
            }

            if (slocation.length > 0) {
                params.append('location', slocation.join(','));
            }

            if (city.length > 0) {
                params.append('city', city.join(','));
            }

            if (activeSearch) {
                params.append('search', activeSearch);
            }

            // If no filters are active, reset filteredItems and return
            if (params.toString() === '') {
                setFilteredItems([]);
                setLoading(false);
                return;
            }

            const endpoint = `${API_URL}/api/filtered_items?${params.toString()}`;
            const response = await fetch(endpoint, {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            });

            const elapsed = performance.now() - t0;
            if (elapsed > 2000) {
                console.error(`[SLOW] /api/filtered_items took ${(elapsed / 1000).toFixed(2)}s (limit: 2s) — params: ${params.toString()}`);
            }

            if (response.ok) {
                const data = await response.json();

                // Client-side verification
                const locationFilterActive = slocation.length > 0 || city.length > 0;
                if (token && locationFilterActive && userId) {
                    const userItems = data.results.filter(item => item.userid == userId);
                    if (userItems.length > 0) {
                        console.error('Client-side validation: User items found in results!', userItems);
                        // Filter out user items on client side as fallback
                        data.results = data.results.filter(item => item.userid != userId);
                    }
                }
                const savedWishlist = JSON.parse(localStorage.getItem("wishlist") || "[]");
                const wishlistedIds = new Set(savedWishlist.map(w => w.id));
                const itemsWithWishlist = (data.results || []).map(item => ({ ...item, isWishlist: wishlistedIds.has(item.id) }));
                setFilteredItems(itemsWithWishlist);
            } else {
                console.error("[Shop] fetchFilteredItems failed:", response.status);
                setFilteredItems([]);
            }
        } catch (error) {
            console.error("[Shop] fetchFilteredItems error:", error);
            setFilteredItems([]);
        } finally {
            setLoading(false);
        }
    }, [merchant, category, slocation, city, activeSearch, API_URL, token, userId]);

    useEffect(() => {
        if (merchant.length > 0 || category.length > 0 || slocation.length > 0 || city.length > 0 || activeSearch) {
            fetchFilteredItems();
        } else {
            setFilteredItems([]);
        }
    }, [fetchFilteredItems, merchant, category, slocation, city, activeSearch]);

    const handleFilterChange = (type, value) => {
        if (type === "merchant") {
            setMerchant(prev =>
                prev.includes(value)
                    ? prev.filter(item => item !== value)
                    : [...prev, value]
            );
        } else if (type === "category") {
            setCategory(prev =>
                prev.includes(value)
                    ? prev.filter(item => item !== value)
                    : [...prev, value]
            );
        } else if (type === "location") {
            setSlocation(prev =>
                prev.includes(value)
                    ? prev.filter(item => item !== value)
                    : [...prev, value]
            );
        } else if (type === "city") {
            setCity(prev =>
                prev.includes(value)
                    ? prev.filter(item => item !== value)
                    : [...prev, value]
            );
        }
    };

    useEffect(() => {
        if (userId) fetchItems(userId);
    }, [currentPage]); // eslint-disable-line react-hooks/exhaustive-deps

    // Fetch all 4 filter lists in parallel — runs alongside fetchItems, no sequential wait
    useEffect(() => {
        const headers = {
            "Authorization": `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json"
        };
        const t0 = performance.now();
        Promise.all([
            fetch(`${API_URL}/api/merchants`,  { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
            fetch(`${API_URL}/api/locations`,  { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
            fetch(`${API_URL}/api/cities`,     { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
            fetch(`${API_URL}/api/categories`, { headers }).then(r => r.ok ? r.json() : null).catch(() => null),
        ]).then(([merchantData, locationData, cityData, categoryData]) => {
            if (merchantData)  setMerchants(merchantData.merchants   || []);
            if (locationData)  setLocations(locationData.locations   || []);
            if (cityData)      setCities(cityData.cities             || []);
            if (categoryData)  setAvailableCategories(categoryData.categories || []);
            const elapsed = performance.now() - t0;
            if (elapsed > 2000) console.error(`[SLOW] filter fetch ${(elapsed/1000).toFixed(2)}s`);
        });
    }, [API_URL]);


    const fetchUserId = useCallback(async () => {
        const stored = localStorage.getItem('userId');
        if (stored && stored !== 'null' && stored !== 'undefined') {
            setUserId(stored);
            fetchItems(stored);
            return;
        }
        if (!_cache) setLoading(true);
        try {
            const res = await fetch(`${API_URL}/api/get-user-id`, {
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
            });
            if (!res.ok) { setLoading(false); setError("Session expired. Please log in again."); return; }
            const data = await res.json();
            const uid = String(data.user_id);
            setUserId(uid);
            localStorage.setItem('userId', uid);
            fetchItems(uid);
        } catch (err) {
            console.error('[Shop] fetchUserId:', err);
            setLoading(false);
            setError("Could not verify your session.");
        }
    }, [token, API_URL, fetchItems]);

    useEffect(() => {
        if (token) { fetchUserId(); }
        else        { setLoading(false); }
    }, [fetchUserId, token]);


    const toggleFilterVisibility = () => {
        setIsFilterOpen(!isFilterOpen);
    };

    const navigateToWishlist = () => navigate('/Wishlist');

    const handleAddToCartClick = async (card) => {
        const t0 = performance.now();
        try {
            setPopupMessage('');
            clearTimeout(popupTimeoutRef.current);

            if (!token) {
                setPopupMessage("Please log in to add items to your cart.");
                popupTimeoutRef.current = setTimeout(() => setPopupMessage(""), 3000);
                return;
            }
            const userId = localStorage.getItem('userId');
            if (!userId) {
                setPopupMessage("Please log out and log back in.");
                popupTimeoutRef.current = setTimeout(() => setPopupMessage(""), 3000);
                return;
            }

            const response = await fetch(`${API_URL}/api/cart_items`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    item_id: card.id,
                    product_name: card.name,
                    quantity: 1,
                    user_id: userId,
                }),
            });

            const elapsed = performance.now() - t0;
            if (elapsed > 2000) {
                console.error(`[SLOW] Add to cart took ${(elapsed / 1000).toFixed(2)}s (limit: 2s) — item_id=${card.id}`);
            }

            if (!response.ok) {
                const errorData = await response.json();
                setPopupMessage(errorData.error || "Failed to add to cart. Please try again.");
                popupTimeoutRef.current = setTimeout(() => setPopupMessage(""), 3000);
                return;
            }

            // Optimistic badge increment — no extra GET /api/get_cart_items round-trip needed
            setCartCount(prev => (typeof prev === 'number' ? prev + 1 : 1));

            const data = await response.json();
            setPopupMessage(data.message === "Item quantity updated in cart"
                ? "Item quantity updated in your cart!"
                : "Item added to cart!");
            popupTimeoutRef.current = setTimeout(() => setPopupMessage(""), 3000);

            // Email notification is now sent server-side in /api/cart_items (background thread)
            // No separate frontend call needed

        } catch (error) {
            console.error("Error adding item to cart:", error);
            setPopupMessage("Failed to add to cart. Please try again.");
            popupTimeoutRef.current = setTimeout(() => setPopupMessage(""), 3000);
        }
    };

    const toggleWishlist = async (id) => {
        if (!userId) {
            setPopupMessage("Please log in to update your wishlist.");
            clearTimeout(popupTimeoutRef.current);
            popupTimeoutRef.current = setTimeout(() => setPopupMessage(""), 2000);
            return;
        }

        const cardToUpdate = itemsToDisplay.find(card => card.id === id);
        if (!cardToUpdate) {
            setPopupMessage("Card not found.");
            clearTimeout(popupTimeoutRef.current);
            popupTimeoutRef.current = setTimeout(() => setPopupMessage(""), 2000);
            return;
        }

        const isWishlist = !cardToUpdate.isWishlist;

        setItems(prevItems =>
            prevItems.map(card =>
                card.id === id ? { ...card, isWishlist } : card
            )
        );

        setFilteredItems(prevItems =>
            prevItems.map(card =>
                card.id === id ? { ...card, isWishlist } : card
            )
        );

        const updatedWishlist = isWishlist
            ? [...wishlist, { ...cardToUpdate, isWishlist: true }]
            : wishlist.filter(item => item.id !== id);

        setWishlist(updatedWishlist);

        localStorage.setItem("wishlist", JSON.stringify(updatedWishlist));
        setPopupMessage(`"${cardToUpdate.name}" ${isWishlist ? 'added to' : 'removed from'} Wishlist!`);
        clearTimeout(popupTimeoutRef.current);
        popupTimeoutRef.current = setTimeout(() => setPopupMessage(""), 2000);

        try {
            const response = await fetch(`${API_URL}/api/shop/${id}/wishlist`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    user_id: userId,
                    isWishlist
                })
            });

            if (!response.ok) {
                const errorData = await response.json();
                console.error("Error updating wishlist:", errorData);
                setPopupMessage(`Error updating wishlist: ${errorData.error || response.statusText}`);
                clearTimeout(popupTimeoutRef.current);
                popupTimeoutRef.current = setTimeout(() => setPopupMessage(""), 2000);
            }
        } catch (error) {
            console.error("Error updating wishlist:", error);
            setPopupMessage("Error updating wishlist.");
            clearTimeout(popupTimeoutRef.current);
            popupTimeoutRef.current = setTimeout(() => setPopupMessage(""), 2000);
        }
    };

    const goToPreviousPage = () => setCurrentPage(prev => Math.max(prev - 1, 1));
    const goToNextPage = () => setCurrentPage(prev => prev + 1);

    useEffect(() => {
        return () => {
            clearTimeout(popupTimeoutRef.current);
        };
    }, []);

    // ── Debounce search — waits 400ms after user stops typing before fetching ─
    useEffect(() => {
        clearTimeout(searchDebounceRef.current);
        if (!searchInput) {
            setActiveSearch('');
            return;
        }
        searchDebounceRef.current = setTimeout(() => {
            setActiveSearch(searchInput);
        }, 400);
        return () => clearTimeout(searchDebounceRef.current);
    }, [searchInput]);

    const handleSearchClick = () => {
        setCurrentPage(1);
        setActiveSearch(searchInput);
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleSearchClick();
        }
    };

    const clearFilters = () => {
        setCategory([]);
        setMerchant([]);
        setSlocation([]);
        setCity([]);
        setSearchInput('');
        setActiveSearch('');
        setCurrentPage(1);
        fetchItems(localStorage.getItem('userId')); // Call fetchItems instead of fetchFilteredItems
    };

    const handleStoreClick = useCallback((pageid, page_name) => {
        navigate(`/store/${pageid}`);
    }, [navigate]);


    return (
        <div className="main-div-shop">
            <Header />
            <div className="shop-div">
                <div className="grid-shop">
                    <div className={`filter ${isFilterOpen ? 'open' : ''}`} style={{ display: !isMobile ? 'block' : (isFilterOpen ? 'block' : 'none') }}>
                        <div className='filter-products-main-box'>
                            <div className='filter-box'>
                                <div className="nav-container-navigation">
                                    <input
                                        className="search-input-navigation"
                                        type="text"
                                        placeholder="Enter product name..."
                                        value={searchInput}
                                        onChange={(e) => setSearchInput(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                    />
                                    <button
                                        className="search-button"
                                        onClick={handleSearchClick}
                                        disabled={loading}
                                    >
                                        {loading ? <div className="small-spinner"></div> : 'Search'}
                                    </button>
                                    {isMobile && (
                                        <button className="close-filter-button" onClick={toggleFilterVisibility}>
                                            Close
                                        </button>
                                    )}
                                </div>

                                {locations.length > 0 && (
                                    <FilterDropdown
                                        title="Location"
                                        options={locations}
                                        selectedOptions={slocation}
                                        onOptionChange={(value) => handleFilterChange("location", value)}
                                        searchPlaceholder="Search locations..."
                                    />
                                )}

                                {cities.length > 0 && (
                                    <FilterDropdown
                                        title="City"
                                        options={cities}
                                        selectedOptions={city}
                                        onOptionChange={(value) => handleFilterChange("city", value)}
                                        searchPlaceholder="Search cities..."
                                    />
                                )}

                                {merchants.length > 0 && (
                                    <FilterDropdown
                                        title="Merchant"
                                        options={merchants}
                                        selectedOptions={merchant}
                                        onOptionChange={(value) => handleFilterChange("merchant", value)}
                                        searchPlaceholder="Search merchants..."
                                    />
                                )}

                                {availableCategories.length > 0 && (
                                    <FilterDropdown
                                        title="Category"
                                        options={availableCategories}
                                        selectedOptions={category}
                                        onOptionChange={(value) => handleFilterChange("category", value)}
                                        searchPlaceholder="Search categories..."
                                    />
                                )}

                                <button onClick={clearFilters} className="clear-filters-btn">
                                    Clear All Filters
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className="shop-cards">
                        <nav className="navigation_shop_card">
                            <button
                                className="filter-button"
                                onClick={toggleFilterVisibility}
                                style={{ display: isMobile && !isFilterOpen ? 'block' : 'none' }}
                            >
                                {isFilterOpen ? 'Close' : 'Filter'}
                            </button>
                            <div className="profile-container-shop-n">
                                <a href="#" onClick={navigateToWishlist}>
                                    <FiHeart className="nav-icons-navbar-s " />
                                </a>
                            </div>
                        </nav>
{/* Update the filter chips container */}
{/* Update the filter chips container */}
<div className="filter-chips-container">
    {slocation.length > 0 && (
        <div className="filter-chip city-filter-chip">
            <span>Location: {slocation.join(", ")}</span>
            <button 
                onClick={() => {
                    setSlocation([]);
                    fetchItems(localStorage.getItem('userId'));
                }}
                className="chip-close-button"
                title="Clear location filter"
            >
                <FaTimes size={10} />
            </button>
        </div>
    )}
    {city.length > 0 && (
        <div className="filter-chip city-filter-chip">
            <span>City: {city.join(", ")}</span>
            <button 
                onClick={() => {
                    setCity([]);
                    fetchItems(localStorage.getItem('userId'));
                }}
                className="chip-close-button"
                title="Clear city filter"
            >
                <FaTimes size={10} />
            </button>
        </div>
    )}
    {merchant.length > 0 && (
        <div className="filter-chip merchant-filter-chip">
            <span>Merchant: {merchant.join(", ")}</span>
            <button 
                onClick={() => {
                    setMerchant([]);
                    fetchItems(localStorage.getItem('userId'));
                }}
                className="chip-close-button"
                title="Clear merchant filter"
            >
                <FaTimes size={10} />
            </button>
        </div>
    )}
    {category.length > 0 && (
        <div className="filter-chip category-filter-chip">
            <span>Category: {category.join(", ")}</span>
            <button 
                onClick={() => {
                    setCategory([]);
                    fetchItems(localStorage.getItem('userId'));
                }}
                className="chip-close-button"
                title="Clear category filter"
            >
                <FaTimes size={10} />
            </button>
        </div>
    )}
</div>
                        <div className="shop_js-shop">
    {loading ? (
        <LoadingSkeleton />
    ) : error ? (
        <div className="error-message">
            <div className="error-icon">⚠️</div>
            <div>{error}</div>
        </div>
    ) : itemsToDisplay && itemsToDisplay.length > 0 ? (
        itemsToDisplay.map(card => (
            <ProductCard
                key={card.id}
                card={card}
                onAddToCart={handleAddToCartClick}
                onToggleWishlist={toggleWishlist}
                onStoreClick={handleStoreClick}
            />
        ))    ) : (
        <div className="no-results-message">
            <div className="no-results-icon">📭</div>
            <h3>No Items Present</h3>
            <p>{(merchant.length || category.length || slocation.length || city.length || activeSearch)
                ? "We couldn't find any products matching your filters."
                : "There are no products available at the moment."}
            </p>
        </div>
    )}
</div>

                        {/*<div className="pagination-container">
                            <button
                                onClick={goToPreviousPage}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </button>
                            <span>Page: {currentPage}</span>
                            <button
                                onClick={goToNextPage}
                            >
                                Next
                            </button>
                        </div>*/}
                    </div>
                </div>
            </div>
            {popupMessage && (
                <div className="wishlist-popup-shop">
                    {popupMessage}
                    {popupMessage.includes("cart is full") && (
                        <button className="goto-cart-button-shop" onClick={() => navigate('/Cart')}>
                            Go to Cart
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

export default Shop;